import { useContext, useEffect, useRef, useState } from "react";
import { AppContext } from "../../App";
import PopUp from "../PopUp";
import api from "../api/api";
import functions from "../../functions/functions";
import LoadIcon from "../../icons/LoadIcon";
import { useAppStore } from "../../store/useAppStore";
import GloryErrorLabel from "../glory/GloryErrorLabel";
import CashmaticErrorLabel from "../cashmatic/CashmaticErrorLabel";
import { billErrorText } from "../../functions/paymentErrors";

// order      : ordine in pagamento (res.data)
// edit       : true se l'ordine esisteva gia' ed e' stato aperto da POS per essere ripagato,
//              false se e' stato creato al volo al momento del pagamento
// previousStatus : (solo edit=true) stato che l'ordine aveva prima che partisse il pagamento
//                  cassa, da ripristinare in caso di annullo/errore
function CashPaymentPopup({ order, edit = false, previousStatus = null }) {
    const { setLoading, setConfirm, init_cart, sendLocalCommand, loadNextSplittedOrder, setPosPaymentPopup } = useContext(AppContext);
    const { setCashPaymentPopup, glory, cashmatic } = useAppStore();
    const isCashmatic = !!order.cashmatic_id;
    const errorField = isCashmatic ? "cashmatic_error" : "glory_error";
    const payedField = isCashmatic ? "cashmatic_cash_payed" : "glory_cash_payed";
    const payedFlagField = isCashmatic ? "cashmatic_payed" : "glory_payed";
    const device = isCashmatic ? cashmatic : glory;
    const ErrorLabel = isCashmatic ? CashmaticErrorLabel : GloryErrorLabel;
    // Pagamento misto contanti + carta: incassati i contanti l'ordine passa al terminale per la quota carta
    const hasCardPayment = (order.payments || []).some((p) => p.payment_method_id == 14);
    const [deviceError, setDeviceError] = useState(null);
    const [payed, setPayed] = useState(0);
    // true quando l'operatore ha premuto "annulla" su un pagamento cashmatic mentre c'era
    // gia' del denaro in gestione dal device: serve a distinguere un successivo
    // "not_dispensed" da annullo (Test 7/8 - ordine NON fatturato, torna nel carrello) da
    // uno durante un pagamento normale a buon fine (Test 6 - ordine fatturato).
    const [cancelRequested, setCancelRequested] = useState(false);
    // Copia in ref: checkOrder gira dentro un setInterval con closure sullo stato al mount.
    const cancelRequestedRef = useRef(false);
    // Evita che checkOrder lanci abortPaymentBackToCart piu' volte prima che il popup chiuda.
    const abortingRef = useRef(false);
    // Contanti incassati (glory_payed/cashmatic_payed = 1): da qui si aspetta solo lo scontrino, che con un
    // registratore lento (es. PAX) puo' richiedere minuti. Il pagamento non si puo' piu' annullare.
    const [paymentDone, setPaymentDone] = useState(false);
    // Scontrino fallito (bill == 2) dopo l'incasso: codice scritto dall'agente in bill_number.
    const [billError, setBillError] = useState(null);

    // active_transaction e' un campo sul DEVICE, non sull'ordine: al momento in cui questo
    // popup si apre puo' contenere ancora i valori dell'ultimo pagamento concluso sullo
    // stesso cashmatic (l'agente lo azzera solo dopo aver ricevuto ed elaborato l'evento
    // socket, con un ritardo di rete di circa 1s). Ignoriamo quindi il valore gia' presente
    // al mount: la tabella deve mostrare 0 finche' non arriva un aggiornamento genuino.
    const initialActiveTransactionRef = useRef(cashmatic?.active_transaction ?? null);

    // Stato live della transazione in corso (Richiesto/Inserito/Erogato/Non erogato),
    // pubblicato dall'agente locale su cashmatic.active_transaction durante il polling.
    let liveTransaction = null;
    if (
        isCashmatic &&
        cashmatic?.active_transaction &&
        cashmatic.active_transaction !== initialActiveTransactionRef.current
    ) {
        try {
            liveTransaction = JSON.parse(cashmatic.active_transaction);
        } catch (e) {
            liveTransaction = null;
        }
    }
    const isFullyCovered =
        liveTransaction && parseFloat(liveTransaction.inserted || 0) >= parseFloat(liveTransaction.requested || 0);

    // L'agente azzera cashmatic.active_transaction non appena il device torna "idle", cioe'
    // proprio nell'istante in cui la transazione termina (successo o errore): senza questo
    // "ultimo valore noto", la tabella si svuoterebbe a 0 esattamente quando compare l'errore
    // (es. "not_dispensed"), il momento in cui i valori servono di piu' all'operatore.
    const [lastLiveTransaction, setLastLiveTransaction] = useState(null);
    useEffect(() => {
        if (liveTransaction) setLastLiveTransaction(liveTransaction);
    }, [cashmatic?.active_transaction]);
    const displayedTransaction = liveTransaction || lastLiveTransaction;

    useEffect(() => {
        checkOrder();
        const versionTimeout = window.setInterval(() => checkOrder(), 5000);
        return () => clearInterval(versionTimeout);
    }, []);

    const checkOrder = async () => {
        const res = await api.postProtected("/orders/get/" + order.id + "/", {
            depth: 0,
            fields: ["id", "status", "bill", "bill_number", errorField, payedField, payedFlagField],
        });
        if (res.success != 1) {
            setConfirm({ title: "Errore", message: res.error });
            return;
        }
        const isPayed = res.data[payedFlagField] == 1;
        setPayed(res.data[payedField] ? parseFloat(res.data[payedField]) : 0);
        // Contanti incassati, resta la quota carta (o la carta e' gia' stata rifiutata): si passa al popup del
        // pagamento elettronico, che mostra l'esito e il Riprova. edit: true perche' annullando li' l'ordine
        // non va archiviato (i contanti sono gia' nella cassa).
        if (hasCardPayment && isPayed && (res.data.status == "awaiting_payment" || res.data.status == "payment_error")) {
            setCashPaymentPopup(false);
            setPosPaymentPopup({ data: order, edit: true });
            return;
        }
        // Contanti incassati ma scontrino fallito: l'operatore deve vederlo prima di chiudere.
        if (isPayed && res.data.status != "archived" && res.data.bill == 2) {
            setDeviceError(null);
            setPaymentDone(true);
            setBillError(billErrorText(res.data.bill_number) || "Errore scontrino");
            return;
        }
        // Pagamento concluso e scontrinato, oppure ordine archiviato altrove: chiudi e resetta.
        if (
            res.data.status == "archived" ||
            res.data.bill == 2 ||
            res.data.bill == 3 ||
            res.data.bill == 4
        ) {
            closeAndReset(res.data.status);
            return;
        }
        if (isPayed && res.data.status != "payment_error") {
            setDeviceError(null);
            setPaymentDone(true);
            return;
        }
        // Annullo cashmatic concluso "pulito": l'agente ha segnalato cashmatic_error="canceled"
        // (denaro inserito e poi tutto restituito dal device). Riportiamo l'ordine nello stato
        // giusto, manteniamo il carrello e chiudiamo.
        if (isCashmatic && cancelRequestedRef.current && res.data[errorField] == "canceled") {
            await abortPaymentBackToCart();
            return;
        }
        setDeviceError(res.data[errorField]);
    };

    // Pagamento concluso: svuota il carrello, passa all'eventuale parte successiva di un ordine diviso e chiudi.
    const closeAndReset = (status) => {
        init_cart();
        if (status != "archived") loadNextSplittedOrder();
        setCashPaymentPopup(false);
        window.parent.postMessage("close", "*");
    };

    // Uscita dal pagamento contanti senza incasso (annullo operatore, device offline,
    // errore dispositivo, importo insufficiente, denaro/resto restituito a mano dopo annullo...):
    // - ordine creato al volo   -> "unconfirmed" (bozza recuperabile)
    // - ordine gia' aperto da POS -> ripristina lo stato che aveva prima del pagamento cassa
    // Mai comanda, mai scontrino. Il carrello mantiene i prodotti (niente init_cart) e il
    // POS resta aperto (niente postMessage "close"): l'operatore puo' ripagare o cambiare metodo.
    const abortPaymentBackToCart = async () => {
        if (abortingRef.current) return;
        abortingRef.current = true;
        setLoading(true);
        const targetStatus = edit ? previousStatus || "confirmed" : "unconfirmed";
        const res = await api.postProtected("/orders/edit/" + order.id + "/", {
            status: targetStatus,
            [errorField]: null,
        });
        setLoading(false);
        if (res.success == 1) {
            setDeviceError(null);
            setCashPaymentPopup(false);
        } else {
            abortingRef.current = false;
            setConfirm({ title: "Errore", message: res.error });
        }
    };

    // Chiusura da uno stato di errore mostrato all'operatore (device offline, errore
    // dispositivo, importo insufficiente, "Importo restituito" dopo annullo con not_dispensed):
    // manda prima l'annullo al device per chiudere l'eventuale transazione ancora aperta.
    const abortFromErrorState = async () => {
        if (isCashmatic) {
            await sendLocalCommand("cancelPaymentRequest", { cashmatic_id: order.cashmatic_id });
        } else {
            await sendLocalCommand("changeCancelRequest", { glory_id: order.glory_id });
        }
        await abortPaymentBackToCart();
    };

    const cancel = async () => {
        setConfirm({
            title: "Attenzione",
            message: "Sei sicuro di annullare il pagamento?",
            onConfirm: async () => {
                setLoading(true);
                if (isCashmatic) {
                    await sendLocalCommand("cancelPaymentRequest", { cashmatic_id: order.cashmatic_id });
                    setCancelRequested(true);
                    cancelRequestedRef.current = true;
                    const inserted = parseFloat(displayedTransaction?.inserted || 0);
                    const dispensed = parseFloat(displayedTransaction?.dispensed || 0);
                    if (inserted > dispensed) {
                        // Il device ha ancora del denaro in gestione: l'esito arriva async da
                        // pollTransactionLoop (cashmatic.js) -> cashmatic_error "canceled" se
                        // riesce a restituire tutto (lo gestisce checkOrder), "not_dispensed" se
                        // non ci riesce (ramo not_dispensed+cancelRequested nel render).
                        setLoading(false);
                        return;
                    }
                    // Niente in sospeso (0 inseriti o gia' tutto restituito): risolvi subito.
                    await abortPaymentBackToCart();
                    return;
                }
                // Glory: comportamento invariato (ordine archiviato, carrello svuotato).
                await sendLocalCommand("changeCancelRequest", { glory_id: order.glory_id });
                const res = await api.postProtected("/orders/edit/" + order.id + "/", {
                    status: "archived",
                });
                setLoading(false);
                if (res.success == 1) {
                    init_cart();
                    setCashPaymentPopup(false);
                    window.parent.postMessage("close", "*");
                } else {
                    setConfirm({
                        title: "Errore",
                        message: res.error,
                    });
                }
            },
        });
    };

    const setCompleted = async () => {
        setLoading(true);
        // Caso cashmatic "not_dispensed" durante un pagamento valido (non annullo): il device
        // ha incassato il richiesto ma non e' riuscito a erogare il resto. cashmatic_cash_payed
        // e' stato impostato dall'agente al resto ancora da restituire (non al lordo consegnato),
        // per mostrare qui il messaggio "restituire X manualmente". Ora che l'operatore conferma
        // di aver dato il resto a mano, va corretto al lordo (totale ordine + resto), altrimenti
        // lo scontrino fiscale verrebbe stampato con l'importo del resto invece del totale ordine.
        // Va mandato in una POST separata PRIMA della conferma: should_emit_order_socket_edit
        // (api/class/orders.php) usa la sola presenza di "cashmatic_cash_payed" nel body come
        // euristica "questo edit arriva dall'agente, non ri-notificare" per evitare loop
        // stampa->patch->evento->stampa - se lo includiamo nella STESSA richiesta che conferma
        // l'ordine, quella notifica (necessaria per far stampare l'agente) verrebbe soppressa
        // anche se la richiesta arriva dal POS.
        if (isCashmatic) {
            await api.postProtected("/orders/edit/" + order.id + "/", {
                cashmatic_cash_payed: getAmount() + payed,
            });
        }
        // pagamento misto: niente scontrino qui, lo emette il ramo pagamento elettronico dopo la carta
        const res = await api.postProtected(
            "/orders/edit/" + order.id + "/",
            hasCardPayment
                ? { status: "awaiting_payment", [payedFlagField]: 1, [errorField]: null }
                : { status: "confirmed", bill: 1, [payedFlagField]: 1, [errorField]: null },
        );
        setLoading(false);
        if (res.success == 1) {
            setDeviceError(null);
            setPaymentDone(true);
        } else {
            setConfirm({
                title: "Errore",
                message: res.error,
            });
        }
    };

    // Solo Glory (codice 9): l'importo inserito va restituito a mano e l'ordine annullato.
    const setCanceled = async () => {
        setLoading(true);
        const res = await api.postProtected("/orders/edit/" + order.id + "/", {
            status: "archived",
            [errorField]: null,
        });
        setLoading(false);
        if (res.success == 1) {
            setDeviceError(null);
            init_cart();
            setCashPaymentPopup(false);
            window.parent.postMessage("close", "*");
        } else {
            setConfirm({
                title: "Errore",
                message: res.error,
            });
        }
    };

    const getAmount = () => {
        if (order.payments.length > 0) {
            let total = 0;
            order.payments.forEach((payment) => {
                if (payment.payment_method_id == 1) {
                    total += parseFloat(payment.amount);
                }
            });
            return total;
        } else {
            return order.total;
        }
    };

    return (
        <>
            <PopUp
                customClass="electronic-payment"
                size=""
                options={{
                    onClose: !deviceError && !isFullyCovered && !paymentDone ? () => cancel() : false,
                    title: "Pagamento Contanti",
                }}
            >
                {paymentDone && (
                    <div className="inner-content">
                        {!billError && (
                            <span>
                                <LoadIcon></LoadIcon>
                            </span>
                        )}
                        <h3>Pagamento ricevuto</h3>
                        {!isCashmatic && (
                            <div className="total-import">
                                <h3>Importo totale:</h3>{" "}
                                <h1>
                                    {functions.formatter.format(payed)}/{functions.formatter.format(getAmount())}
                                </h1>
                            </div>
                        )}
                        {!billError && (
                            <>
                                <h5>
                                    Stampa dello scontrino in corso: puoi chiudere, un eventuale errore resta segnalato
                                    nella lista ordini
                                </h5>
                                <button onClick={() => closeAndReset("confirmed")}>Chiudi</button>
                            </>
                        )}
                    </div>
                )}
                {paymentDone && billError && (
                    <div className="inner-content error-retry">
                        <h3>Errore</h3>
                        <h1>{billError}</h1>
                        <h3>Il pagamento è stato incassato: verificare il registratore prima di ristampare lo scontrino</h3>
                        <button onClick={() => closeAndReset("confirmed")}>Chiudi</button>
                    </div>
                )}
                {!paymentDone && (
                    <div className="inner-content">
                        {!deviceError && (
                            <span>
                                <LoadIcon></LoadIcon>
                            </span>
                        )}
                        <h3>Richiesta di pagamento in corso ({device ? device.status : "N/A"})</h3>
                        {!isCashmatic && (
                            <div className="total-import">
                                <h3>Importo totale:</h3>{" "}
                                <h1>
                                    {functions.formatter.format(payed)}/{functions.formatter.format(getAmount())}
                                </h1>
                            </div>
                        )}
                        {!deviceError && <h5>Verifica il dispositivo di pagamento per procedere</h5>}
                        {isCashmatic && (
                            <table className="cashmatic-live-transaction">
                                <tbody>
                                    <tr>
                                        <td>Richiesto</td>
                                        <td>{functions.formatter.format(parseFloat(displayedTransaction?.requested || 0) / 100)}</td>
                                    </tr>
                                    <tr>
                                        <td>Inserito</td>
                                        <td>{functions.formatter.format(parseFloat(displayedTransaction?.inserted || 0) / 100)}</td>
                                    </tr>
                                    <tr>
                                        <td>Erogato</td>
                                        <td>{functions.formatter.format(parseFloat(displayedTransaction?.dispensed || 0) / 100)}</td>
                                    </tr>
                                    <tr>
                                        <td>Non erogato</td>
                                        <td>{functions.formatter.format(parseFloat(displayedTransaction?.notDispensed || 0) / 100)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        )}
                    </div>
                )}
                {!isCashmatic && deviceError == 9 && (
                    <div className="inner-content error-retry">
                        <h3>Errore</h3>
                        <h1>
                            <ErrorLabel code={deviceError} /> ({deviceError})
                        </h1>
                        <h3>Restituire importo di {functions.formatter.format(payed)} manualmente</h3>
                        <button onClick={setCanceled}>Importo consegnato</button>
                    </div>
                )}
                {!isCashmatic && deviceError == 10 && (
                    <div className="inner-content error-retry">
                        <h3>Errore</h3>
                        <h1>
                            <ErrorLabel code={deviceError} /> ({deviceError})
                        </h1>
                        <h3>
                            Restituire resto di {functions.formatter.format(payed - getAmount())} manualmente
                        </h3>
                        <button onClick={setCompleted}>Resto consegnato</button>
                    </div>
                )}
                {/* Altri errori Glory (cassa non collegata, comunicazione interrotta, cassa occupata...): senza questo
                    riquadro il popup restava senza pulsanti e senza chiusura. */}
                {!isCashmatic && deviceError && deviceError != 9 && deviceError != 10 && (
                    <div className="inner-content error-retry">
                        <h3>Errore</h3>
                        <h1>
                            <ErrorLabel code={deviceError} /> ({deviceError})
                        </h1>
                        {payed > 0 && (
                            <h3>Verificare sulla cassa i contanti inseriti ({functions.formatter.format(payed)})</h3>
                        )}
                        <button onClick={abortFromErrorState}>Annulla pagamento</button>
                    </div>
                )}
                {isCashmatic && deviceError == "not_dispensed" && !cancelRequested && (
                    <div className="inner-content error-retry">
                        <h3>Errore</h3>
                        <h1>
                            <ErrorLabel code={deviceError} />
                        </h1>
                        <h3>
                            Il denaro e&apos; stato inserito correttamente ma il dispositivo non e&apos; riuscito a
                            restituire il resto: restituire l&apos;intero resto di {functions.formatter.format(payed)} manualmente
                        </h3>
                        <button onClick={setCompleted}>Resto consegnato</button>
                    </div>
                )}
                {isCashmatic && deviceError == "not_dispensed" && cancelRequested && (
                    <div className="inner-content error-retry">
                        <h3>Errore</h3>
                        <h1>
                            <ErrorLabel code={deviceError} />
                        </h1>
                        <h3>
                            Il pagamento e&apos; stato annullato ma il dispositivo non e&apos; riuscito a restituire
                            il denaro inserito: restituire l&apos;importo di {functions.formatter.format(payed)} manualmente
                        </h3>
                        <button onClick={abortFromErrorState}>Importo restituito</button>
                    </div>
                )}
                {isCashmatic &&
                    deviceError &&
                    deviceError != "not_dispensed" &&
                    deviceError != "canceled" &&
                    deviceError != "connection_error" &&
                    deviceError != "insufficient_amount" &&
                    deviceError != 11 &&
                    deviceError != 12 && (
                        <div className="inner-content error-retry">
                            <h3>Errore</h3>
                            <h1>
                                <ErrorLabel code={deviceError} /> ({deviceError})
                            </h1>
                            <button onClick={abortFromErrorState}>Annulla pagamento</button>
                        </div>
                    )}
                {isCashmatic &&
                    (deviceError == "connection_error" ||
                        deviceError == "insufficient_amount" ||
                        deviceError == 11 ||
                        deviceError == 12) && (
                        <div className="inner-content error-retry">
                            <h3>Errore</h3>
                            <h1>
                                <ErrorLabel code={deviceError} />
                            </h1>
                            <button onClick={abortFromErrorState}>Annulla pagamento</button>
                        </div>
                    )}
            </PopUp>
        </>
    );
}

export default CashPaymentPopup;
